// VeneersData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string;
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const veneers: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    en: {
        "url": "",
        "meta": {
            "title": "Veneers Dubai",
            "description": "Transform your smile with veneers in Dubai at Elite Body Home Polyclinic, the leading clinic for smile makeovers.",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Veneers Dubai"],
        "hero": {
            "title": "Dental Veneers in Dubai",
            "subtitle": "Transform your smile with veneers in Dubai",
            "description": "Whether addressing chips, gaps, or discoloration, our custom-crafted solutions provide natural, long-lasting results. From composite veneers to porcelain, we ensure a precise fit tailored to your unique smile. Boost your confidence with a flawless, radiant look!"
        },
        "howItWorks": {
            "title": "What Are Veneers?",
            "description": "Veneers are thin, custom-made shells that cover the front surface of teeth to improve their appearance. Porcelain or composite resin forms, they rectify discoloration, chips, gaps, and minor misalignment. Veneers offer a natural, durable solution for a perfect smile with minimal alteration of the tooth."
        },
        "procedureSteps": {
            "heading": "Procedure for Getting Veneers in Dubai",
            "para": "",
            "steps": [
                {
                    "title": "Consultation and Assessment",
                    "desc": "Talk to our cosmetic dentist about your veneer options and smile goals.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Teeth Preparation",
                    "desc": "A thin coating of enamel is shaved to get a precise fit, followed by impressions for the custom veneers.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Temporary Veneers (If Needed)",
                    "desc": "Temporary veneers may be placed to protect your teeth while the permanent ones are being fabricated.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Custom Veneer Fabrication",
                    "desc": "Porcelain, composite, ceramic, E-Max or zirconia veneers of superior quality to those found in a dental lab",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "Final Fitting & Bonding",
                    "desc": "They are then checked for fit and shade before being bonded permanently to your teeth.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Aftercare & Follow-up",
                    "desc": "With regular check-ups and good oral hygiene, results can last a lifetime.",
                    "icon": "fa-solid fa-hand-holding-medical"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Dental Veneers in Dubai",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "Instant Smile Enhancement",
                    "desc": "Fixes discoloration, chips, gaps, and minor misalignment.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Natural Appearance",
                    "desc": "Blends seamlessly with your teeth.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "Long-Lasting",
                    "desc": "Porcelain lasts 10-15 years, composite 5-7 years.",
                    "icon": "fa-solid fa-hourglass-end"
                },
                {
                    "title": "Stain-Resistant",
                    "desc": "Porcelain veneers resist discoloration.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "Minimally Invasive",
                    "desc": "Preserves most of your natural tooth structure.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Quick Results",
                    "desc": "Complete transformation in 1-3 visits.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Boosts Confidence",
                    "desc": "A flawless smile for a lasting impression.",
                    "icon": "fa-solid fa-face-grin-stars"
                }
            ]
        },
        "eligibility": {
            "heading": "Ideal Candidates for Veneers",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "Healthy Gums and Teeth",
                    "desc": "Free of decay or disease.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Sufficient Enamel",
                    "desc": "Enough enamel for veneer bonding.",
                    "icon": "fa-solid fa-shield"
                },
                {
                    "title": "Mild to Moderate Imperfections",
                    "desc": "Ideal for minor cosmetic issues.",
                    "icon": "fa-solid fa-sliders"
                },
                {
                    "title": "No Teeth Grinding",
                    "desc": "Bruxism sufferers may need a mouthguard.",
                    "icon": "fa-solid fa-moon"
                }
            ]
        },
        "preparationTips": {
            "heading": "Quick Facts About Veneers",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "Type of Treatment",
                    "desc": "Cosmetic dental procedure",
                    "icon": "fa-solid fa-stethoscope"
                },
                {
                    "title": "Materials",
                    "desc": "Composite resin or porcelain",
                    "icon": "fa-solid fa-box-open"
                },
                {
                    "title": "Price",
                    "desc": "3,999 to 25,000 AED (depending on the number of teeth)",
                    "icon": "fa-solid fa-tag"
                },
                {
                    "title": "Uses",
                    "desc": "Fill chips, gaps and discolored spots, plus minor misalignment",
                    "icon": "fa-solid fa-wrench"
                },
                {
                    "title": "Durability",
                    "desc": "10–15 years for porcelain; 5–7 years for composite",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Number of Appointments",
                    "desc": "2-3 visits for porcelain; one visit for composite",
                    "icon": "fa-solid fa-calendar-days"
                },
                {
                    "title": "Pain Level",
                    "desc": "Minor discomfort, possibly treated with local anesthesia",
                    "icon": "fa-solid fa-face-grimace"
                },
                {
                    "title": "Staining",
                    "desc": "Porcelain resists stains; composite can discolor after a while",
                    "icon": "fa-solid fa-paintbrush"
                }
            ]
        },
        "results": {
            "heading": "Before & After Results",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "Natural-Looking Transformation",
                    "desc": "Achieve a flawless, radiant smile that looks completely natural.",
                    "icon": "fa-solid fa-face-grin"
                },
                {
                    "title": "Long-Term Solution",
                    "desc": "Enjoy your new smile for years with proper care.",
                    "icon": "fa-solid fa-calendar-plus"
                },
                {
                    "title": "Enhanced Confidence",
                    "desc": "Smile freely without hesitation or self-consciousness.",
                    "icon": "fa-solid fa-face-laugh-beam"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Keeping Your Veneers",
            "subheading": "",
            "para": "Regular Brushing, Flossing & Dental Check-ups",
            "items": [
                {
                    "title": "Oral Hygiene",
                    "desc": "Maintain regular brushing and flossing routines.",
                    "icon": "fa-solid fa-toothbrush"
                },
                {
                    "title": "Dental Check-ups",
                    "desc": "Schedule regular visits to monitor your veneers.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Avoid Hard Foods",
                    "desc": "Prevent damage by avoiding biting hard objects.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Stain Prevention",
                    "desc": "Limit consumption of staining beverages like coffee or tea.",
                    "icon": "fa-solid fa-mug-hot"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long do veneers typically last?",
                    "answer": "Porcelain veneers last 10-15 years, while composite veneers last 5-7 years with proper care and regular dental check-ups."
                },
                {
                    "question": "Are veneers a permanent solution?",
                    "answer": "Veneers are semi-permanent since some enamel is removed. While long-lasting, they may need replacement over time due to wear or damage."
                },
                {
                    "question": "Can veneers fix crooked teeth?",
                    "answer": "Veneers can correct mild misalignment, but severe cases may require orthodontic treatments like braces or Invisalign for proper alignment."
                },
                {
                    "question": "Do veneers stain over time?",
                    "answer": "Porcelain veneers are stain-resistant, but composite veneers may discolor."
                }
            ]
        },
        "treatmentAreas1": [
            {
                "ar": "",
                "title": "Porcelain Veneers",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/9.jpg"
            },
            {
                "ar": "",
                "title": "Composite Veneers",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/7.jpg"
            },
            {
                "ar": "",
                "title": "Ceramic Veneers",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/5.jpg"
            }
        ],
        "treatmentAreas2": [
            {
                "ar": "",
                "title": "E-Max Veneers",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/2.jpg"
            },
            {
                "ar": "",
                "title": "Zirconia Veneers",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/4.jpg"
            }
        ]
    },
    ar: {
        "url": "",
        "meta": {
            "title": "فينير الأسنان في دبي",
            "description": "تُعد عيادات ايليت بودي هوم أفضل عيادات لتجميل الابتسامة عبر الفينير في دبي",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "فينير الأسنان"],
        "hero": {
            "title": "فينير الأسنان في دبي",
            "subtitle": "تحويل ابتسامتك مع أفضل عيادات الفينير",
            "description": "سواء كنت تعاني من تصبغات، أو فراغات بين الأسنان، أو تشققات، نوفر لك حلول مخصصة لتحقيق نتائج طبيعية تدوم طويلًا. نُصمّم الفينير بدقة من الكمبوزيت أو البورسلين، ليناسب ابتسامتك الفريدة."
        },
        "howItWorks": {
            "title": "ما هو الفينير؟",
            "description": "قشور رقيقة تُصمم خصيصًا لتغطي السطح الأمامي للأسنان لتحسين مظهرها. سواء من البورسلين أو الكمبوزيت، تُستخدم لتصحيح التصبغات والتشققات والفجوات والانحرافات البسيطة، تمنحك ابتسامة طبيعية مع الحفاظ على بنية السن الأصلية."
        },
        "procedureSteps": {
            "heading": "خطوات تركيب الفينير في دبي",
            "para": "",
            "steps": [
                {
                    "title": "الاستشارة والتقييم",
                    "desc": "جلسة مع طبيب الأسنان التجميلي لمناقشة الخيارات المناسبة لابتسامتك",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "تحضير الأسنان",
                    "desc": "إزالة طبقة رقيقة من المينا وأخذ طبعات الأسنان",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "فينير مؤقت (حسب الحاجة)",
                    "desc": "تركيب قشور مؤقتة لحماية الأسنان أثناء الانتظار",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "تصنيع الفينير",
                    "desc": "تصميم القشور الدائمة من مواد عالية الجودة في المختبر",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "التركيب النهائي",
                    "desc": "تثبيت الفينير الدائم بعد التحقق من الملاءمة واللون",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "المتابعة",
                    "desc": "زيارات متابعة لضمان نجاح العلاج على المدى الطويل",
                    "icon": "fa-solid fa-hand-holding-medical"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد فينير الأسنان في دبي",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "تحسين فوري للابتسامة",
                    "desc": "معالجة التصبغات والتشققات والفجوات بسرعة",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "مظهر طبيعي",
                    "desc": "اندماج كامل مع الأسنان الطبيعية",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "متانة طويلة الأمد",
                    "desc": "البورسلين يدوم 15 سنة والكمبوزيت 7 سنوات",
                    "icon": "fa-solid fa-hourglass-end"
                },
                {
                    "title": "مقاومة التصبغ",
                    "desc": "خاصة مع قشور البورسلين",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "غير جراحي",
                    "desc": "الحفاظ على بنية السن الأصلية",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "نتائج سريعة",
                    "desc": "بين 1-3 زيارات فقط",
                    "icon": "fa-solid fa-bolt"
                }
            ]
        },
        "eligibility": {
            "heading": "المرشحون المثاليون للفينير",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "صحة الفم الجيدة",
                    "desc": "خالي من التسوس وأمراض اللثة",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "سمك مينا كافي",
                    "desc": "لضمان التصاق جيد للفينير",
                    "icon": "fa-solid fa-shield"
                },
                {
                    "title": "مشاكل تجميلية خفيفة",
                    "desc": "لتصحيح العيوب البسيطة",
                    "icon": "fa-solid fa-sliders"
                },
                {
                    "title": "مرضى صرير الأسنان",
                    "desc": "يحتاجون لواقي ليلي",
                    "icon": "fa-solid fa-moon"
                }
            ]
        },
        "preparationTips": {
            "heading": "معلومات سريعة عن الفينير",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "نوع الإجراء",
                    "desc": "تجميلي غير جراحي",
                    "icon": "fa-solid fa-stethoscope"
                },
                {
                    "title": "المواد",
                    "desc": "بورسلين أو كمبوزيت أو سيراميك",
                    "icon": "fa-solid fa-box-open"
                },
                {
                    "title": "التكلفة",
                    "desc": "3,999 إلى 25,000 درهم",
                    "icon": "fa-solid fa-tag"
                },
                {
                    "title": "المدة",
                    "desc": "البورسلين 10-15 سنة، الكمبوزيت 5-7 سنوات",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "عدد الجلسات",
                    "desc": "2-3 جلسات للبورسلين، جلسة واحدة للكمبوزيت",
                    "icon": "fa-solid fa-calendar-days"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "ابتسامة مثالية",
                    "desc": "مظهر طبيعي ومتناسق",
                    "icon": "fa-solid fa-face-grin"
                },
                {
                    "title": "ثقة بالنفس",
                    "desc": "ابتسامة جذابة بدون تحفظ",
                    "icon": "fa-solid fa-face-laugh-beam"
                },
                {
                    "title": "حل طويل الأمد",
                    "desc": "مع العناية الصحيحة",
                    "icon": "fa-solid fa-calendar-plus"
                }
            ]
        },
        "aftercareTips": {
            "heading": "العناية بعد التركيب",
            "subheading": "",
            "para": "نصائح للحفاظ على الفينير",
            "items": [
                {
                    "title": "النظافة اليومية",
                    "desc": "تفريش الأسنان واستخدام الخيط",
                    "icon": "fa-solid fa-toothbrush"
                },
                {
                    "title": "الفحوصات الدورية",
                    "desc": "زيارة الطبيب بانتظام",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "تجنب الأطعمة القاسية",
                    "desc": "للحماية من التلف",
                    "icon": "fa-solid fa-utensils"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "هل الفينير مناسب للجميع؟",
                    "answer": "نعم مع استثناءات قليلة مثل أمراض اللثة الحادة أو صرير الأسنان الشديد"
                },
                {
                    "question": "هل يسبب الفينير الألم؟",
                    "answer": "قد يكون هناك انزعاج بسيط يمكن التحكم فيه بمخدر موضعي"
                },
                {
                    "question": "كم تستغرق عملية التركيب؟",
                    "answer": "البورسلين يحتاج 2-3 زيارات بينما الكمبوزيت يكفي له زيارة واحدة"
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    shared: {
        images: {
            main: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Veneers-Header.jpg",
            beforeAfter: [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/sdfsdgs-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/sdfsdgs-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/dfgdrgd.jpg"
            ],
            introductionImage: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Tooth-Filling-Service-Pagdgdre.jpg   ",
        },
        product: {
            id: 26,
            title: "Veneers",
            price: 499,
            description: "Transform your smile with veneers in Dubai at Elite Body Home Polyclinic, the leading clinic for smile makeovers."
        }
    }
};